/**
 * Convert normalized bounding box [y_min, x_min, y_max, x_max] (0â€“1000)
 * to pixel coordinates {x, y, width, height} based on the given image size [w, h].
 */
export function normalizedToPixel(normalized_bbox, imageSize) {
  if (!normalized_bbox || normalized_bbox.length !== 4 || !imageSize || imageSize.length !== 2 || imageSize[0] <= 0 || imageSize[1] <= 0) {
    console.warn('Invalid input to normalizedToPixel:', normalized_bbox, imageSize);
    return { x: 0, y: 0, width: 10, height: 10 }; // Return a default small box
  }
  const [w, h] = imageSize;
  const [y_min, x_min, y_max, x_max] = normalized_bbox;

  // Clamp values to be safe, although backend should ensure 0-1000
  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);
  const yMinClamped = clamp(y_min, 0, 1000);
  const xMinClamped = clamp(x_min, 0, 1000);
  const yMaxClamped = clamp(y_max, yMinClamped, 1000); // Ensure yMax >= yMin
  const xMaxClamped = clamp(x_max, xMinClamped, 1000); // Ensure xMax >= xMin


  const pixel_y_min = Math.round((yMinClamped / 1000) * h);
  const pixel_x_min = Math.round((xMinClamped / 1000) * w);
  const pixel_y_max = Math.round((yMaxClamped / 1000) * h);
  const pixel_x_max = Math.round((xMaxClamped / 1000) * w);

  // Ensure width/height are non-negative
  const width = Math.max(0, pixel_x_max - pixel_x_min);
  const height = Math.max(0, pixel_y_max - pixel_y_min);

  return {
    x: pixel_x_min,
    y: pixel_y_min,
    width: width,
    height: height
  };
}

/**
 * Convert a pixel bounding box { x, y, width, height } to normalized
 * [y_min, x_min, y_max, x_max] values (0â€“1000) based on the image size [w, h].
 */
export function pixelToNormalized(pixel_bbox, imageSize) {
   if (!pixel_bbox || !imageSize || imageSize.length !== 2 || imageSize[0] <= 0 || imageSize[1] <= 0) {
     console.warn('Invalid input to pixelToNormalized:', pixel_bbox, imageSize);
    return [0, 0, 0, 0];
  }
  const [w, h] = imageSize;
  const { x, y, width, height } = pixel_bbox;

  // Ensure coordinates are within image bounds
  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);
  const x1 = clamp(x, 0, w);
  const y1 = clamp(y, 0, h);
  const x2 = clamp(x + width, x1, w); // Ensure x2 >= x1
  const y2 = clamp(y + height, y1, h); // Ensure y2 >= y1


  // Convert clamped pixel coordinates to normalized (0-1000 range)
  // Use Math.round to get integers as expected by the backend schema (INTEGER[])
  const y_min = Math.round((y1 / h) * 1000);
  const x_min = Math.round((x1 / w) * 1000);
  const y_max = Math.round((y2 / h) * 1000);
  const x_max = Math.round((x2 / w) * 1000);

  // Ensure max >= min even after rounding
  return [
      Math.min(y_min, y_max), // y_min
      Math.min(x_min, x_max), // x_min
      Math.max(y_min, y_max), // y_max
      Math.max(x_min, x_max)  // x_max
   ];
}
